import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  Badge,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  EmojiEmotions as EmojiIcon,
  MoreVert as MoreVertIcon,
  Flag as FlagIcon,
  ArrowBack as BackIcon,
  Store as StoreIcon,
  Person as PersonIcon,
  Circle as OnlineIcon,
  CircleOutlined as OfflineIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  Message as MessageIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  LocalOffer as OfferIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { Message, Conversation } from '../../services/messageService';
import OfferMessageCard from './OfferMessageCard';
import OfferDecisionMessageCard from './OfferDecisionMessageCard';
import { MessageService } from '../../services/messageService';
import { useAuthStore } from '../../store/authStore';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import ChatInfoPopup from './ChatInfoPopup';
import ImagePreviewPopup from './ImagePreviewPopup';
import ImageMessage from './ImageMessage';
import DocumentPreviewPopup from './DocumentPreviewPopup';
import DocumentMessage from './DocumentMessage';
import VideoPreviewPopup from './VideoPreviewPopup';
import VideoMessage from './VideoMessage';
import ReportConversationDialog from './ReportConversationDialog';
import { ImageUploadService } from '../../services/imageUploadService';
import { DocumentUploadService } from '../../services/documentUploadService';
import { VideoUploadService } from '../../services/videoUploadService';
import { OffersService, Offer } from '../../services/offersService';
import OfferPopup from './OfferPopup';
import OfferStatusPopup from './OfferStatusPopup';
import MyOffersPopup from './MyOffersPopup';
import AttachmentPopup from './AttachmentPopup';
import EmojiPicker from './EmojiPicker';
import UserAvatar from '../Profile/UserAvatar';
import CompleteTransactionPopup from '../Settings/tabs/CompleteTransactionPopup';
import { getCachedProductThumbnailSrc } from '../../utils/productThumbnailCache';
import { ReportService, type ReportReason } from '../../services/reportService';

interface ChatWindowProps {
  conversation?: Conversation;
  onBack?: () => void;
  headerOpen?: boolean;
  onHeaderOpenChange?: (open: boolean) => void;
}

const isUuid = (value: string | undefined | null): boolean => {
  const v = String(value || '').trim();
  if (!v) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(v);
};

const ChatWindow: React.FC<ChatWindowProps> = ({ conversation, onBack, headerOpen: headerOpenProp, onHeaderOpenChange }) => {
  const { user } = useAuthStore();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chatInfoOpen, setChatInfoOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [partnerUserInfo, setPartnerUserInfo] = useState<any>(null);
  const [currentUserInfo, setCurrentUserInfo] = useState<any>(null);
  const [productThumbSrc, setProductThumbSrc] = useState<string | null>(null);
  const lastMessageIdRef = useRef<string | null>(null);
  const pollTimerRef = useRef<number | null>(null);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageUploadError, setImageUploadError] = useState<string | null>(null);
  
  // Document upload states
  const [documentPreviewOpen, setDocumentPreviewOpen] = useState(false);
  const [selectedDocumentFile, setSelectedDocumentFile] = useState<File | null>(null);
  const [documentPreview, setDocumentPreview] = useState<string | null>(null);
  const [documentUploading, setDocumentUploading] = useState(false);
  const [documentUploadError, setDocumentUploadError] = useState<string | null>(null);
  
  // Video upload states
  const [videoPreviewOpen, setVideoPreviewOpen] = useState(false);
  const [selectedVideoFile, setSelectedVideoFile] = useState<File | null>(null);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoUploadError, setVideoUploadError] = useState<string | null>(null);
  // Track withdrawn offers locally for optimistic UI
  const [withdrawnOfferIds, setWithdrawnOfferIds] = useState<Set<string>>(new Set());
  // Track latest own-offer message id to gray out older ones
  const [latestOwnOfferCreatedAt, setLatestOwnOfferCreatedAt] = useState<string | null>(null);
  // Cache of offers by id for rendering latest offer details
  const [offerById, setOfferById] = useState<Record<string, Offer>>({});
  const lastOfferMessageAtByOfferIdRef = useRef<Record<string, string>>({});
  const offersRealtimeChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const [offerDecisionLoadingId, setOfferDecisionLoadingId] = useState<string | null>(null);
  // Offer popup state
  const [offerOpen, setOfferOpen] = useState(false);
  const [offerLoading, setOfferLoading] = useState(false);
  const [editOfferOpen, setEditOfferOpen] = useState(false);
  const [editingOfferId, setEditingOfferId] = useState<string | null>(null);
  const [editingOfferData, setEditingOfferData] = useState<{price: number, message: string} | null>(null);
  const [withdrawConfirmOpen, setWithdrawConfirmOpen] = useState(false);
  const [withdrawingOfferId, setWithdrawingOfferId] = useState<string | null>(null);
  const [myOffersOpen, setMyOffersOpen] = useState(false);
  // Offer status popup state
  const [offerStatusPopupOpen, setOfferStatusPopupOpen] = useState(false);
  const [offerStatusData, setOfferStatusData] = useState<{
    productName: string;
    productPrice: number;
    acceptedOfferPrice?: number;
    sellerName: string;
    reason: string;
  } | null>(null);

  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedOrderForPayment, setSelectedOrderForPayment] = useState<any>(null);

  const [isOwnConversationProduct, setIsOwnConversationProduct] = useState<boolean>(false);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const documentInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const [attachmentPopupOpen, setAttachmentPopupOpen] = useState(false);
  const [internalHeaderOpen, setInternalHeaderOpen] = useState(false);
  const headerOpen = typeof headerOpenProp === 'boolean' ? headerOpenProp : internalHeaderOpen;
  const setHeaderOpen = (open: boolean) => {
    if (onHeaderOpenChange) onHeaderOpenChange(open);
    else setInternalHeaderOpen(open);
  };

  const handleAcceptOfferFromChat = async (offerId: string) => {
    if (!conversation?.other_user?.id || !conversation?.product?.id) return;
    setOfferDecisionLoadingId(offerId);
    try {
      const updated = await OffersService.acceptOffer(offerId);
      setOfferById(prev => ({ ...prev, [offerId]: updated }));

      const content = `OFFER_DECISION|status=accepted|offerId=${offerId}|price=${updated.offer_price}|text=${updated.offer_message}`;
      await MessageService.sendMessage(
        conversation.other_user.id,
        content,
        'text',
        conversation.product.id,
        conversation.subject,
        { offerId },
        conversation.id
      );
    } catch (err: any) {
      const specific = err?.message || err?.error || 'Unknown error';
      const details = err?.details ? ` Details: ${err.details}` : '';
      const hint = err?.hint ? ` Hint: ${err.hint}` : '';
      const code = err?.code ? ` (code: ${err.code})` : '';
      setError(`Failed to accept offer: ${specific}${code}${details}${hint}`);
    } finally {
      setOfferDecisionLoadingId(null);
    }
  };

  const handlePayNowForOfferFromChat = async (offerId: string) => {
    try {
      const { data: purchase, error } = await supabase
        .from('purchases')
        .select('*')
        .eq('offer_id', offerId)
        .limit(1)
        .maybeSingle();

      if (error) {
        throw new Error(error.message || 'Failed to load purchase');
      }

      if (!purchase) {
        throw new Error('No order found for this offer yet. Please open Orders and try again in a moment.');
      }

      if ((purchase as any).paid_status === 'succeeded') {
        toast.success('Payment already completed');
        return;
      }

      setSelectedOrderForPayment(purchase);
      setPaymentModalOpen(true);
    } catch (e: any) {
      const msg = e?.message || 'Failed to open payment';
      toast.error(msg);
    }
  };

  const handleRejectOfferFromChat = async (offerId: string) => {
    if (!conversation?.other_user?.id || !conversation?.product?.id) return;
    setOfferDecisionLoadingId(offerId);
    try {
      const updated = await OffersService.declineOffer(offerId);
      setOfferById(prev => ({ ...prev, [offerId]: updated }));

      const content = `OFFER_DECISION|status=rejected|offerId=${offerId}|price=${updated.offer_price}|text=${updated.offer_message}`;
      await MessageService.sendMessage(
        conversation.other_user.id,
        content,
        'text',
        conversation.product.id,
        conversation.subject,
        { offerId },
        conversation.id
      );
    } catch (err: any) {
      const specific = err?.message || err?.error || 'Unknown error';
      const details = err?.details ? ` Details: ${err.details}` : '';
      const hint = err?.hint ? ` Hint: ${err.hint}` : '';
      const code = err?.code ? ` (code: ${err.code})` : '';
      setError(`Failed to reject offer: ${specific}${code}${details}${hint}`);
    } finally {
      setOfferDecisionLoadingId(null);
    }
  };
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);

  // Fetch user information from the users table
  const fetchUserInfo = async () => {
    if (!conversation || !user) return;
    
    try {
      const otherUserId = conversation.other_user?.id;
      
      // Fetch partner user info
      if (otherUserId) {
        const { data: partnerData, error: partnerError } = await supabase
          .from('users')
          .select('id, first_name, last_name, alias')
          .eq('id', otherUserId)
          .single();
        
        if (partnerError) {
          console.error('Error fetching partner user info:', partnerError);
        } else {
          setPartnerUserInfo(partnerData);
        }
      }
      
      // Fetch current user info
      const { data: currentData, error: currentError } = await supabase
        .from('users')
        .select('id, first_name, last_name, alias')
        .eq('id', user.id)
        .single();
      
      if (currentError) {
        console.error('Error fetching current user info:', currentError);
      } else {
        setCurrentUserInfo(currentData);
      }
    } catch (error) {
      console.error('Error fetching user info:', error);
    }
  };

  // Keep an up-to-date cache of offers referenced by messages so edited offers show latest values
  useEffect(() => {
    const offerMessages = (messages || []).filter(
      (m) => !!m.offer_id && (m.message_type === 'offer' || (m.content || '').startsWith('NEW_OFFER'))
    );
    if (offerMessages.length === 0) return;

    const uniqueOfferIds = Array.from(new Set(offerMessages.map(m => m.offer_id).filter((id): id is string => !!id)));
    const idsToFetch = uniqueOfferIds.filter((id) => {
      const newest = offerMessages
        .filter(m => m.offer_id === id)
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
      if (!newest) return false;
      const lastSeenAt = lastOfferMessageAtByOfferIdRef.current[id];
      return !lastSeenAt || new Date(newest.created_at).getTime() > new Date(lastSeenAt).getTime();
    });

    if (idsToFetch.length === 0) return;

    let cancelled = false;
    (async () => {
      try {
        const results = await Promise.all(idsToFetch.map(id => OffersService.getOfferById(id)));
        if (cancelled) return;
        const next: Record<string, Offer> = {};
        results.forEach((off, idx) => {
          const id = idsToFetch[idx];
          if (off) next[id] = off;
        });
        idsToFetch.forEach((id) => {
          const newest = offerMessages
            .filter(m => m.offer_id === id)
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
          if (newest?.created_at) lastOfferMessageAtByOfferIdRef.current[id] = newest.created_at;
        });
        if (Object.keys(next).length > 0) {
          setOfferById(prev => ({ ...prev, ...next }));
        }
      } catch (e) {
        console.warn('Failed to fetch offers for messages', e);
      }
    })();

    return () => { cancelled = true; };
  }, [messages]);

  const latestOfferMessageCreatedAtByOfferId = useMemo(() => {
    const map: Record<string, string> = {};
    for (const m of messages || []) {
      if (!m.offer_id) continue;
      if (!(m.message_type === 'offer' || (m.content || '').startsWith('NEW_OFFER'))) continue;
      const prev = map[m.offer_id];
      if (!prev) {
        map[m.offer_id] = m.created_at;
        continue;
      }
      try {
        if (new Date(m.created_at).getTime() > new Date(prev).getTime()) map[m.offer_id] = m.created_at;
      } catch {
        // ignore
      }
    }
    return map;
  }, [messages]);

  // Subscribe to realtime updates on offers referenced by this conversation
  useEffect(() => {
    const offerIds = Array.from(
      new Set(
        (messages || [])
          .filter(m => !!m.offer_id && (m.message_type === 'offer' || (m.content || '').startsWith('NEW_OFFER')))
          .map(m => m.offer_id)
          .filter((id): id is string => !!id)
      )
    );

    // Clean up any existing subscription
    if (offersRealtimeChannelRef.current) {
      try {
        supabase.removeChannel(offersRealtimeChannelRef.current);
      } catch {
        // ignore
      }
      offersRealtimeChannelRef.current = null;
    }

    if (!conversation?.id || offerIds.length === 0) return;

    const filter = `id=in.(${offerIds.join(',')})`;
    const channel = supabase
      .channel(`offers-${conversation.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'offers', filter },
        (payload: any) => {
          const next = payload?.new as Offer | undefined;
          if (!next?.id) return;
          setOfferById(prev => ({ ...prev, [next.id]: next }));
        }
      )
      .subscribe();

    offersRealtimeChannelRef.current = channel;

    return () => {
      try {
        supabase.removeChannel(channel);
      } catch {
        // ignore
      }
      if (offersRealtimeChannelRef.current === channel) {
        offersRealtimeChannelRef.current = null;
      }
    };
  }, [conversation?.id, messages]);

  // Fetch user info when conversation changes
  React.useEffect(() => {
    if (conversation) {
      fetchUserInfo();
    }
  }, [conversation]);

  useEffect(() => {
    const productId = String((conversation as any)?.product_id || conversation?.product?.id || '').trim();
    if (!productId || !user?.id) {
      setIsOwnConversationProduct(false);
      return;
    }

    const sellerFromValidation = (conversation as any)?.offerValidation?.productInfo?.seller_id;
    if (typeof sellerFromValidation === 'string' && sellerFromValidation.length > 0) {
      setIsOwnConversationProduct(sellerFromValidation === user.id);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from('products')
          .select('seller_id')
          .eq('id', productId)
          .maybeSingle();
        if (cancelled) return;
        setIsOwnConversationProduct((data as any)?.seller_id === user.id);
      } catch {
        if (cancelled) return;
        setIsOwnConversationProduct(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [conversation?.id, (conversation as any)?.product_id, conversation?.product?.id, (conversation as any)?.offerValidation?.productInfo?.seller_id, user?.id]);

  // Auto-scroll to bottom function
  const scrollToBottom = () => {
    const messagesContainer = messagesEndRef.current?.parentElement?.parentElement;
    if (messagesContainer) {
      // Try direct scrollTop first
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
      
      // Also try scrollIntoView as backup
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
    }
  };

  // Auto-scroll to bottom when messages load or change
  React.useEffect(() => {
    // Always scroll to bottom when messages change, regardless of count
    setTimeout(scrollToBottom, 100);
  }, [messages]);

  // Auto-scroll to bottom when conversation changes (when clicking on a conversation tab)
  React.useEffect(() => {
    if (conversation) {
      // Use longer timeout to ensure messages are loaded first
      setTimeout(scrollToBottom, 500);
      // Also try again after a longer delay to catch any async loading
      setTimeout(scrollToBottom, 1000);
    }
  }, [conversation?.id]);

  // Auto-scroll when component mounts with messages
  React.useEffect(() => {
    if (messages.length > 0) {
      setTimeout(scrollToBottom, 100);
    }
  }, []);

  // Handle image file selection
  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate image file
    const validation = ImageUploadService.validateImageFile(file);
    if (!validation.valid) {
      setImageUploadError(validation.error || 'Invalid image file');
      return;
    }

    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setSelectedImageFile(file);
    setImagePreview(previewUrl);
    setImagePreviewOpen(true);
    setImageUploadError(null);

    // Clear the input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle image send
  const handleImageSend = async (altText: string, compressedFile: File) => {
    if (!compressedFile || !conversation || !user) return;

    setImageUploading(true);
    setImageUploadError(null);

    try {
      console.log('Starting image upload process...');
      
      // Generate a unique message ID first
      const messageId = crypto.randomUUID();
      
      // Upload compressed image to Supabase storage first
      const uploadResult = await ImageUploadService.uploadImage(
        compressedFile,
        user.id,
        messageId
      );

      if (!uploadResult.success) {
        console.error('Image upload failed:', uploadResult.error);
        setImageUploadError(uploadResult.error || 'Failed to upload image');
        setImageUploading(false);
        return;
      }

      console.log('Image uploaded successfully:', uploadResult);

      // Now create the message using MessageService with image data
      try {
        const messageData = await MessageService.sendMessage(
          conversation.other_user?.id || '',
          altText || '', // Use alt text as content
          'image',
          conversation.product_id,
          conversation.subject,
          {
            imageUrl: uploadResult.imageUrl,
            imagePath: uploadResult.imagePath,
            imageFilename: uploadResult.imageData?.filename || compressedFile.name,
            imageSize: uploadResult.imageData?.size || compressedFile.size,
            imageMimeType: uploadResult.imageData?.mimeType || compressedFile.type,
            imageWidth: uploadResult.imageData?.width || 0,
            imageHeight: uploadResult.imageData?.height || 0,
            imageAltText: altText || undefined,
          },
          conversation.id // Pass conversation ID
        );

        console.log('Message created with image:', messageData);
      } catch (messageError: any) {
        console.error('Error creating message:', messageError);
        setImageUploadError('Failed to create message: ' + (messageError.message || messageError));
        setImageUploading(false);
        return;
      }

      // Refresh messages
      await loadMessages();
      
      // Close popup and reset state
      setImagePreviewOpen(false);
      setSelectedImageFile(null);
      setImagePreview(null);
      setImageUploading(false);

      console.log('Image message sent successfully!');

    } catch (error) {
      console.error('Error sending image:', error);
      setImageUploadError('Failed to send image: ' + (error as Error).message);
      setImageUploading(false);
    }
  };

  // Handle file input click
  const handleFileInputClick = () => {
    fileInputRef.current?.click();
  };

  const handleAttachmentClick = () => {
    setAttachmentPopupOpen(true);
  };

  // Handle document file selection
  const handleDocumentSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate document file
    if (!DocumentUploadService.isSupportedDocumentType(file)) {
      setDocumentUploadError('Unsupported file type. Please select a Word document, PowerPoint presentation, Excel spreadsheet, PDF, or other supported office file.');
      return;
    }

    if (file.size > 50 * 1024 * 1024) { // 50MB limit
      setDocumentUploadError('File too large. Maximum size is 50MB.');
      return;
    }

    setSelectedDocumentFile(file);
    setDocumentUploadError(null);
    setDocumentPreviewOpen(true);
  };

  // Handle document send
  const handleDocumentSend = async (description: string, documentFile: File) => {
    if (!documentFile || !conversation || !user) return;

    setDocumentUploading(true);
    setDocumentUploadError(null);

    try {
      console.log('Starting document upload process...');
      
      // Generate a unique message ID first
      const messageId = crypto.randomUUID();
      
      // Upload document to Supabase storage first
      const uploadResult = await DocumentUploadService.uploadDocument(
        documentFile,
        user.id,
        messageId
      );

      if (!uploadResult.success) {
        console.error('Document upload failed:', uploadResult.error);
        setDocumentUploadError(uploadResult.error || 'Failed to upload document');
        setDocumentUploading(false);
        return;
      }

      console.log('Document uploaded successfully:', uploadResult);

      // Now create the message using MessageService with document data
      try {
        const messageData = await MessageService.sendMessage(
          conversation.other_user?.id || '',
          description || '', // Use description as content
          'document',
          conversation.product_id,
          conversation.subject,
          {
            documentUrl: uploadResult.documentUrl,
            documentPath: uploadResult.documentPath,
            documentFilename: uploadResult.documentData?.filename || documentFile.name,
            documentSize: uploadResult.documentData?.size || documentFile.size,
            documentMimeType: uploadResult.documentData?.mimeType || documentFile.type,
            documentExtension: DocumentUploadService.getFileExtension(documentFile.name),
            documentCategory: DocumentUploadService.getDocumentCategory(documentFile.type),
          },
          conversation.id // Pass conversation ID
        );

        console.log('Message created with document:', messageData);
      } catch (messageError: any) {
        console.error('Error creating message:', messageError);
        setDocumentUploadError('Failed to create message: ' + (messageError.message || messageError));
        setDocumentUploading(false);
        return;
      }

      // Refresh messages
      await loadMessages();
      
      // Close popup and reset state
      setDocumentPreviewOpen(false);
      setSelectedDocumentFile(null);
      setDocumentPreview(null);
      setDocumentUploading(false);

      console.log('Document message sent successfully!');

    } catch (error) {
      console.error('Error sending document:', error);
      setDocumentUploadError('Failed to send document: ' + (error as Error).message);
      setDocumentUploading(false);
    }
  };

  // Handle document input click
  const handleDocumentInputClick = () => {
    documentInputRef.current?.click();
  };

  // Handle video file selection
  const handleVideoSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate video file
    if (!VideoUploadService.isSupportedVideoType(file)) {
      setVideoUploadError('Unsupported video type. Please select MP4, WebM, OGG, AVI, MOV, WMV, FLV, MKV, 3GP, or QuickTime video.');
      return;
    }

    if (file.size > 100 * 1024 * 1024) { // 100MB limit
      setVideoUploadError('Video too large. Maximum size is 100MB.');
      return;
    }

    setSelectedVideoFile(file);
    setVideoUploadError(null);
    setVideoPreviewOpen(true);
  };

  // Handle video send
  const handleVideoSend = async (description: string, videoFile: File) => {
    if (!videoFile || !conversation || !user) return;

    setVideoUploading(true);
    setVideoUploadError(null);

    try {
      console.log('Starting video upload process...');
      
      // Generate a unique message ID first
      const messageId = crypto.randomUUID();
      
      // Upload video to Supabase storage
      const uploadResult = await VideoUploadService.uploadVideo(
        videoFile,
        user.id,
        messageId
      );

      if (!uploadResult.success) {
        console.error('Video upload failed:', uploadResult.error);
        setVideoUploadError(uploadResult.error || 'Failed to upload video');
        setVideoUploading(false);
        return;
      }

      console.log('Video uploaded successfully:', uploadResult);

      // Now create the message using MessageService with video data
      try {
        const messageData = await MessageService.sendMessage(
          conversation.other_user?.id || '',
          description || '', // Use description as content
          'video',
          conversation.product_id,
          conversation.subject,
          {
            videoUrl: uploadResult.videoUrl,
            videoPath: uploadResult.videoPath,
            videoFilename: uploadResult.videoData?.filename || videoFile.name,
            videoSize: uploadResult.videoData?.size || videoFile.size,
            videoMimeType: uploadResult.videoData?.mimeType || videoFile.type,
            videoDuration: uploadResult.videoData?.duration || 0,
            videoWidth: uploadResult.videoData?.width || 0,
            videoHeight: uploadResult.videoData?.height || 0,
            videoThumbnailUrl: uploadResult.thumbnailUrl,
            videoThumbnailPath: uploadResult.thumbnailPath,
          },
          conversation.id // Pass conversation ID
        );

        console.log('Video message created successfully:', messageData);
      } catch (messageError: any) {
        console.error('Error creating video message:', messageError);
        setVideoUploadError('Failed to create video message: ' + (messageError.message || messageError));
        setVideoUploading(false);
        return;
      }

      // Refresh messages to show the new video message
      await loadMessages();

      // Close preview and reset state
      setVideoPreviewOpen(false);
      setSelectedVideoFile(null);
      setVideoPreview(null);
      setVideoUploadError(null);
      setVideoUploading(false);

    } catch (error) {
      console.error('Error sending video:', error);
      setVideoUploadError('Failed to send video: ' + (error as Error).message);
      setVideoUploading(false);
    }
  };

  // Handle video input click
  const handleVideoInputClick = () => {
    videoInputRef.current?.click();
  };

  // Open offer popup if conversation has product context
  const handleOpenOffer = async () => {
    if (!conversation?.product?.id) {
      setError('Offers are only available in product-linked conversations.');
      return;
    }
    
    // Run offer validation on-demand
    try {
      const validation = await OffersService.canMakeOfferForProduct(conversation.product.id);
      
      if (!validation.canMakeOffer) {
        // Show the offer status popup for critical reasons
        const criticalReasons = [
          'accepted offer',
          'no longer available',
          'own product'
        ];
        
        const shouldShowPopup = criticalReasons.some(reason => 
          validation.reason?.toLowerCase().includes(reason)
        );
        
        if (shouldShowPopup) {
          setOfferStatusData({
            productName: conversation.product.name,
            productPrice: conversation.product.price,
            acceptedOfferPrice: validation.acceptedOfferPrice,
            sellerName: partnerUserInfo?.alias || partnerUserInfo?.first_name || conversation.other_user?.firstName || 'Vendor',
            reason: validation.reason || 'Cannot make offers'
          });
          setOfferStatusPopupOpen(true);
          return;
        } else {
          // For non-critical reasons, just show an error message
          setError(validation.reason || 'Cannot make an offer at this time');
          return;
        }
      }
      
      // If validation passes, open the offer popup
      setOfferOpen(true);
    } catch (error) {
      console.error('Error validating offer:', error);
      setError('Failed to validate offer eligibility. Please try again.');
    }
  };


  // Submit edit offer from popup
  const handleEditOfferSubmit = async (data: { price: number; message: string }) => {
    if (!editingOfferId || !conversation || !conversation.product?.id || !conversation.other_user?.id) return;
    setOfferLoading(true);
    try {
      console.log('ChatWindow - Updating offer:', editingOfferId, 'with data:', data);
      
      // Update the offer in the database
      const updatedOffer = await OffersService.updateOffer(editingOfferId, {
        offer_price: data.price,
        offer_message: data.message,
      });

      // Update the offerById cache with the new data
      setOfferById(prev => ({
        ...prev,
        [editingOfferId]: updatedOffer
      }));

      console.log('ChatWindow - Offer updated successfully:', updatedOffer);

      // Broadcast an updated offer message so the other user sees the change immediately
      try {
        await MessageService.sendOfferMessage(
          conversation.other_user.id,
          conversation.product.id,
          editingOfferId,
          data.price,
          data.message,
          conversation.subject,
          conversation.id
        );
      } catch (e) {
        console.warn('ChatWindow - Failed to send offer update message', e);
      }

      // Refresh messages to reflect the latest offer state
      try {
        await loadMessages();
      } catch {}

      setEditOfferOpen(false);
      setEditingOfferId(null);
      setEditingOfferData(null);
    } catch (err: any) {
      console.error('ChatWindow - Error updating offer:', err);
      const specific = err?.message || err?.error || 'Unknown error';
      const details = err?.details ? ` Details: ${err.details}` : '';
      const hint = err?.hint ? ` Hint: ${err.hint}` : '';
      const code = err?.code ? ` (code: ${err.code})` : '';
      setError(`Failed to update offer: ${specific}${code}${details}${hint}`);
    } finally {
      setOfferLoading(false);
    }
  };

  // Submit offer from popup
  const handleOfferSubmit = async (data: { price: number; message: string }) => {
    if (!conversation || !conversation.product?.id || !conversation.other_user?.id) return;
    setOfferLoading(true);
    try {
      const created = await OffersService.createOffer({
        product_id: conversation.product.id,
        seller_id: conversation.other_user.id,
        offer_price: data.price,
        offer_message: data.message,
      });

      // Update the offerById cache immediately with the new offer data
      console.log('ChatWindow - Updating offerById cache with new offer:', {
        offerId: created.id,
        offerPrice: created.offer_price,
        offerMessage: created.offer_message
      });
      setOfferById(prev => ({
        ...prev,
        [created.id]: created
      }));

      await MessageService.sendOfferMessage(
        conversation.other_user.id,
        conversation.product.id,
        created.id,
        data.price,
        data.message,
        conversation.subject,
        conversation.id
      );

      setOfferOpen(false);
    } catch (err: any) {
      const specific = err?.message || err?.error || 'Unknown error';
      const details = err?.details ? ` Details: ${err.details}` : '';
      const hint = err?.hint ? ` Hint: ${err.hint}` : '';
      const code = err?.code ? ` (code: ${err.code})` : '';
      setError(`Failed to send offer: ${specific}${code}${details}${hint}`);
    } finally {
      setOfferLoading(false);
    }
  };

  // Test function to check user data directly
  const testUserData = async (userId: string) => {
    console.log('=== TESTING USER DATA ===');
    console.log('Testing user data for ID:', userId);
    console.log('Current user ID:', user?.id);
    
    // Test 1: Get specific user
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId);
    
    console.log('Test 1 - Specific user query:', { data, error });
    if (data && !Array.isArray(data)) {
      const userData = data as any;
      console.log('Test 1 - Alias found:', userData.alias);
      console.log('Test 1 - First name:', userData.first_name);
      console.log('Test 1 - Last name:', userData.last_name);
    } else if (Array.isArray(data)) {
      console.log('Test 1 - Data is array, first item:', data[0]);
    }
    
    // Test 1.5: Try using the get_user_alias function
    const { data: aliasData, error: aliasError } = await supabase
      .rpc('get_user_alias', { user_id: userId });
    
    console.log('Test 1.5 - get_user_alias function:', { aliasData, aliasError });
    
    // Test 2: Get all users to see if we can read the table
    const { data: allUsers, error: allUsersError } = await supabase
      .from('users')
      .select('id, alias, first_name, last_name, user_status_preference')
      .limit(10);
    
    console.log('Test 2 - All users query:', { allUsers, allUsersError });
    if (allUsers) {
      console.log('Test 2 - All aliases found:', allUsers.map(u => ({ id: u.id, alias: u.alias })));
    }
    
    // Test 3: Check current user's own data
    const { data: currentUser, error: currentUserError } = await supabase
      .from('users')
      .select('*')
      .eq('id', user?.id);
    
    console.log('Test 3 - Current user data:', { currentUser, currentUserError });
    
    // Test 4: Check if RLS is enabled
    const { data: rlsData, error: rlsError } = await supabase
      .from('pg_policies')
      .select('*')
      .eq('tablename', 'users');
    
    console.log('Test 4 - RLS policies for users table:', { rlsData, rlsError });
    if (currentUser && !Array.isArray(currentUser)) {
      const userData = currentUser as any;
      console.log('Test 3 - Current user alias:', userData.alias);
    } else if (Array.isArray(currentUser)) {
      console.log('Test 3 - Current user data is array, first item:', currentUser[0]);
    }
    
    // Test 4: Try a simple query without RLS
    const { data: simpleTest, error: simpleError } = await supabase
      .from('users')
      .select('alias')
      .eq('id', userId);
    
    console.log('Test 4 - Simple alias query:', { simpleTest, simpleError });
    
    console.log('=== END TEST ===');
    return { data, error };
  };


  // Note: Offer validation is now handled on-demand when user tries to make an offer
  // This prevents the popup from showing incorrectly when conversations load

  // When conversation changes, reset offer state and load messages
  useEffect(() => {
    if (conversation) {
      setInitialLoadComplete(false);
      // Reset any per-conversation derived state so previous conversation UI doesn't bleed through
      setWithdrawnOfferIds(new Set());
      setLatestOwnOfferCreatedAt(null);
      if (isUuid(conversation.id)) {
        loadMessages();
      } else {
        setMessages([]);
        setError(null);
        setInitialLoadComplete(true);
      }
      // Test the partner's user data
      if (conversation.other_user?.id) {
        testUserData(conversation.other_user.id);
      }
    } else {
      setMessages([]);
      setWithdrawnOfferIds(new Set());
      setLatestOwnOfferCreatedAt(null);
      setInitialLoadComplete(false);
    }
  }, [conversation?.id]);

  // Realtime: subscribe to new messages for this conversation
  useEffect(() => {
    if (!conversation || !user?.id) return;
    if (!isUuid(conversation.id)) return;

    const subscriptionPromise = MessageService.subscribeToConversationMessages(
      conversation.id,
      (message) => {
        setMessages(prev => {
          if (prev.some(m => m.id === message.id)) return prev;
          return [...prev, message];
        });
        setTimeout(scrollToBottom, 100);
      }
    );

    // Also listen to local sends (instant emit on successful insert)
    const offLocal = MessageService.onLocalMessage((message) => {
      // Only append if this message belongs to this conversation
      if (message.conversation_id === conversation.id) {
        setMessages(prev => {
          if (prev.some(m => m.id === message.id)) return prev;
          return [...prev, message];
        });
        setTimeout(scrollToBottom, 100);
      }
    });

    return () => {
      try { subscriptionPromise?.then(ch => { try { ch?.unsubscribe?.(); } catch {} }); } catch {}
      try { offLocal?.(); } catch {}
    };
  }, [conversation?.id, user?.id]);

  // Fallback polling: ensures partner messages appear even if realtime isn't delivering mirror-table UPDATEs
  useEffect(() => {
    if (!conversation || !user?.id) return;
    if (!isUuid(conversation.id)) return;

    const poll = async () => {
      try {
        const latest = await MessageService.getMessagesForConversation(conversation.id, 50, 0);
        const lastId = latest?.length ? latest[latest.length - 1]?.id : null;
        if (lastId && lastId !== lastMessageIdRef.current) {
          lastMessageIdRef.current = lastId;
          setMessages(prev => {
            const prevLast = prev?.length ? prev[prev.length - 1]?.id : null;
            if (prevLast === lastId && prev.length === latest.length) return prev;
            return latest;
          });
          setTimeout(scrollToBottom, 100);
        }
      } catch {
        // ignore polling failures
      }
    };

    // Start after a short delay to avoid racing initial load
    pollTimerRef.current = window.setInterval(poll, 2500);
    const initialTimeout = window.setTimeout(poll, 600);

    return () => {
      try { window.clearTimeout(initialTimeout); } catch {}
      try { if (pollTimerRef.current) window.clearInterval(pollTimerRef.current); } catch {}
      pollTimerRef.current = null;
    };
  }, [conversation?.id, user?.id]);

  const loadMessages = async () => {
    if (!conversation) return;
    if (!isUuid(conversation.id)) {
      setMessages([]);
      setInitialLoadComplete(true);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      console.log('ChatWindow - Loading messages for conversation:', conversation);
      console.log('ChatWindow - User ID:', user?.id);
      console.log('ChatWindow - Other user ID:', conversation.other_user?.id);
      
      const conversationMessages = await MessageService.getMessagesForConversation(
        conversation.id,
        50,
        0
      );
      
      console.log('ChatWindow - Loaded messages:', conversationMessages);
      setMessages(conversationMessages);
      try {
        lastMessageIdRef.current = conversationMessages?.length ? conversationMessages[conversationMessages.length - 1]?.id : null;
      } catch {}

      // Determine latest own-offer timestamp so older offers can be grayed out after reload
      try {
        const latestOwnOffer = [...(conversationMessages || [])]
          .filter(m => m.sender_id === (user?.id || '') && (m.message_type === 'offer' || (m.content || '').startsWith('NEW_OFFER')))
          .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
        setLatestOwnOfferCreatedAt(latestOwnOffer ? latestOwnOffer.created_at : null);
      } catch (e) {
        console.warn('Failed to compute latest own offer timestamp:', e);
      }

      // Determine withdrawn offers on reload by checking missing offer rows
      try {
        const offerIds = Array.from(new Set(
          (conversationMessages || [])
            .map(m => m.offer_id)
            .filter((id): id is string => !!id)
        ));
        if (offerIds.length > 0) {
          const { data: existingOffers, error: offersErr } = await supabase
            .from('offers')
            .select('id')
            .in('id', offerIds);
          if (offersErr) {
            console.warn('Failed to fetch offers to compute withdrawn state:', offersErr);
          } else {
            const existingIds = new Set((existingOffers || []).map(o => o.id as string));
            const withdrawnIds = offerIds.filter(id => !existingIds.has(id));
            setWithdrawnOfferIds(new Set(withdrawnIds));
          }
        } else {
          setWithdrawnOfferIds(new Set());
        }
      } catch (e) {
        console.warn('Error determining withdrawn offers:', e);
      }
      
      // Auto-scroll to bottom after messages are loaded
      setTimeout(scrollToBottom, 200);
      // Also try again after a longer delay to ensure DOM is updated
      setTimeout(scrollToBottom, 500);
      
      // Mark messages as read
      await MessageService.markConversationMessagesAsRead(conversation.id, user?.id || '');
    } catch (err: any) {
      console.error('ChatWindow - Error loading messages:', err);
      const specific = err?.message || err?.error || 'Unknown error';
      const details = err?.details ? ` Details: ${err.details}` : '';
      const hint = err?.hint ? ` Hint: ${err.hint}` : '';
      const code = err?.code ? ` (code: ${err.code})` : '';
      setError(`Failed to load messages: ${specific}${code}${details}${hint}`);
    } finally {
      setLoading(false);
      setInitialLoadComplete(true);
    }
  };

  const composerDisabled = sending || loading || !initialLoadComplete || !conversation || !isUuid(conversation.id);

  const reportDisabled = !conversation || !isUuid(conversation.id) || !user?.id || !conversation.other_user?.id;

  const handleSubmitConversationReport = async (payload: { subject: string; reason: ReportReason; message: string }) => {
    if (!conversation || !isUuid(conversation.id)) {
      toast.error('Please wait until the conversation is fully loaded, then try again.');
      return;
    }
    if (!user?.id) {
      toast.error('You must be signed in to report.');
      return;
    }
    const partnerId = conversation.other_user?.id;
    if (!partnerId) {
      toast.error('Missing chat partner information.');
      return;
    }

    try {
      await ReportService.submitConversationReport({
        reporterUserId: user.id,
        sellerUserId: partnerId,
        conversationId: conversation.id,
        subject: payload.subject,
        reason: payload.reason,
        message: payload.message,
      });
      toast.success('Report submitted. Thank you.');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to submit report');
      throw e;
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !conversation || sending) return;

    console.log('ChatWindow - handleSendMessage called with:', {
      conversationId: conversation.id,
      conversationSubject: conversation.subject,
      otherUserId: conversation.other_user?.id,
      productId: conversation.product_id,
      messageContent: newMessage.trim()
    });

    try {
      setSending(true);
      const message = await MessageService.sendMessage(
        conversation.other_user?.id || '',
        newMessage.trim(),
        'text',
        conversation.product_id,
        conversation.subject,
        undefined,
        conversation.id // Pass the current conversation ID to ensure message goes to the right conversation
      );
      
      console.log('ChatWindow - Message sent successfully:', {
        messageId: message.id,
        conversationId: message.conversation_id,
        content: message.content
      });
      
      setNewMessage('');
      // Auto-scroll to bottom after sending message
      setTimeout(scrollToBottom, 100);
    } catch (err: any) {
      // Surface detailed supabase error info to help diagnose
      const specific = err?.message || err?.error || 'Unknown error';
      const details = err?.details ? ` Details: ${err.details}` : '';
      const hint = err?.hint ? ` Hint: ${err.hint}` : '';
      const code = err?.code ? ` (code: ${err.code})` : '';
      const composed = `Failed to send message: ${specific}${code}${details}${hint}`;
      console.error('Error sending message (detailed):', err);
      setError(composed);
    } finally {
      setSending(false);
    }
  };

  // When an offer is successfully sent locally, set latestOwnOfferCreatedAt
  React.useEffect(() => {
    const offLocal = MessageService.onLocalMessage((message) => {
      if (message.message_type === 'offer' || (message.content || '').startsWith('NEW_OFFER')) {
        if (message.sender_id === user?.id) {
          setLatestOwnOfferCreatedAt(message.created_at);
        }
      }
    });
    return () => {
      try { offLocal?.(); } catch {}
    };
  }, [user?.id]);

  React.useEffect(() => {
    if (!conversation) {
      setProductThumbSrc(null);
      return;
    }

    const productId = String((conversation as any)?.product_id || conversation.product?.id || '').trim();
    if (!productId) {
      setProductThumbSrc(null);
      return;
    }

    const cached = getCachedProductThumbnailSrc(productId);
    if (cached) {
      setProductThumbSrc(cached);
      return;
    }

    const fallbackUrl = (conversation as any)?.product?.images?.[0];
    setProductThumbSrc(typeof fallbackUrl === 'string' && fallbackUrl.trim() ? fallbackUrl.trim() : null);
  }, [(conversation as any)?.product_id, conversation?.product?.id]);

  // Removed Test message helper

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatMessageTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = (now.getTime() - date.getTime()) / (1000 * 60);

    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${Math.floor(diffInMinutes)}m ago`;
    } else if (diffInMinutes < 1440) { // 24 hours
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString();
    }
  };

  const renderMessage = (message: Message, index: number) => {
    const isOwnMessage = message.sender_id === user?.id;
    const showAvatar = index === 0 || messages[index - 1]?.sender_id !== message.sender_id;
    const showTime = index === messages.length - 1 || 
      new Date(message.created_at).getTime() - new Date(messages[index + 1]?.created_at || 0).getTime() > 300000; // 5 minutes

    return (
      <motion.div
        key={message.id}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: isOwnMessage ? 'row-reverse' : 'row',
            alignItems: 'flex-end',
            mb: showTime ? 2 : 0.5,
            px: 2,
          }}
        >
          {/* Avatar */}
          {!isOwnMessage && (
            <Box sx={{ mr: 1, mb: 1 }}>
              {showAvatar ? (
                <UserAvatar
                  src={message.sender?.avatar}
                  name={message.sender?.alias || message.sender?.firstName || 'User'}
                  size={32}
                  sx={{ bgcolor: 'primary.main' }}
                />
              ) : (
                <Box sx={{ width: 32, height: 32 }} />
              )}
            </Box>
          )}

          {/* Message Content */}
          <Box sx={{ maxWidth: '70%', minWidth: (message.message_type === 'image' || message.message_type === 'document' || message.message_type === 'video') ? 'auto' : 100 }}>
            {message.message_type === 'image' ? (
              // Image Message
              <Box>
                <ImageMessage
                  imageUrl={message.image_url || ''}
                  imagePath={message.image_path}
                  imageFilename={message.image_filename}
                  imageSize={message.image_size}
                  imageWidth={message.image_width}
                  imageHeight={message.image_height}
                  imageAltText={message.image_alt_text}
                  isOwnMessage={isOwnMessage}
                  timestamp={message.created_at}
                  senderName={message.sender?.alias || message.sender?.firstName || 'User'}
                />
                
                {/* Image Message Status */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
                  mt: 0.5,
                  gap: 0.5,
                  px: 1,
                }}>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                    }}
                  >
                    {formatMessageTime(message.created_at)}
                  </Typography>
                  {isOwnMessage && (
                    <CheckIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  )}
                </Box>
              </Box>
            ) : message.message_type === 'document' ? (
              // Document Message
              <Box>
                <DocumentMessage
                  documentUrl={message.document_url || ''}
                  documentPath={message.document_path}
                  documentFilename={message.document_filename}
                  documentSize={message.document_size}
                  documentMimeType={message.document_mime_type}
                  documentExtension={message.document_extension}
                  documentCategory={message.document_category}
                  content={message.content}
                  isOwnMessage={isOwnMessage}
                  timestamp={message.created_at}
                  senderName={message.sender?.alias || message.sender?.firstName || 'User'}
                />
                
                {/* Document Message Status */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
                  mt: 0.5,
                  gap: 0.5,
                  px: 1,
                }}>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                    }}
                  >
                    {formatMessageTime(message.created_at)}
                  </Typography>
                  {isOwnMessage && (
                    <CheckIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  )}
                </Box>
              </Box>
            ) : message.message_type === 'video' ? (
              // Video Message
              <Box>
                <VideoMessage
                  videoUrl={message.video_url || ''}
                  videoFilename={message.video_filename}
                  videoSize={message.video_size}
                  videoDuration={message.video_duration}
                  videoWidth={message.video_width}
                  videoHeight={message.video_height}
                  videoThumbnailUrl={message.video_thumbnail_url}
                  content={message.content}
                  isOwnMessage={isOwnMessage}
                  timestamp={message.created_at}
                  senderName={message.sender?.alias || message.sender?.firstName || 'User'}
                />
                
                {/* Video Message Status */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
                  mt: 0.5,
                  gap: 0.5,
                  px: 1,
                }}>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                    }}
                  >
                    {formatMessageTime(message.created_at)}
                  </Typography>
                  {isOwnMessage && (
                    <CheckIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  )}
                </Box>
              </Box>
            ) : (message.content || '').startsWith('OFFER_DECISION') ? (
              // Offer decision message (accepted/rejected)
              <Box>
                {(() => {
                  const statusMatch = /status=([^|]+)/.exec(message.content || '');
                  const decisionStatus = (statusMatch?.[1] || 'rejected').toLowerCase();
                  const decisionOfferId = message.offer_id || (() => {
                    const m = /offerId=([^|]+)/.exec(message.content || '');
                    return m?.[1] || undefined;
                  })();

                  const isAccepted = decisionStatus === 'accepted';
                  const isBuyer = !!decisionOfferId && offerById[decisionOfferId]?.buyer_id === user?.id;

                  return (
                <OfferDecisionMessageCard
                  productTitle={conversation?.product?.name || conversation?.subject || 'Product'}
                  productPrice={conversation?.product?.price || 0}
                  productImageUrl={conversation?.product?.images?.[0]}
                  status={(() => {
                    const match = /status=([^|]+)/.exec(message.content || '');
                    const status = (match?.[1] || 'rejected').toLowerCase();
                    return status === 'accepted' ? 'accepted' : 'rejected';
                  })()}
                  offerPrice={(() => {
                    const match = /price=([^|]+)/.exec(message.content || '');
                    const price = match ? parseFloat(match[1]) : 0;
                    return isNaN(price) ? 0 : price;
                  })()}
                  offerText={(() => {
                    const match = /text=(.*?)(?:\||$)/.exec(message.content || '');
                    return match ? match[1] : '';
                  })()}
                  isOwnMessage={isOwnMessage}
                  showPayNow={isAccepted && isBuyer}
                  onPayNow={decisionOfferId ? () => handlePayNowForOfferFromChat(decisionOfferId) : undefined}
                />
                  );
                })()}
              </Box>
            ) : (message.message_type === 'offer' || (message.content || '').startsWith('NEW_OFFER')) ? (
              // Offer Message (rich card)
              <Box>
                <OfferMessageCard
                  productTitle={conversation?.product?.name || conversation?.subject || 'Product'}
                  productPrice={conversation?.product?.price || 0}
                  productImageUrl={conversation?.product?.images?.[0]}
                  offerPrice={(() => {
                    const isLatestForThisOffer =
                      !!message.offer_id &&
                      latestOfferMessageCreatedAtByOfferId[message.offer_id] === message.created_at;

                    if (isLatestForThisOffer && message.offer_id && offerById[message.offer_id]?.offer_price != null) {
                      return offerById[message.offer_id].offer_price;
                    }
                    
                    const match = /price=([^|]+)/.exec(message.content || '');
                    const price = match ? parseFloat(match[1]) : 0;
                    console.log('ChatWindow - Using parsed offer price:', price, 'for message created at:', message.created_at);
                    return isNaN(price) ? 0 : price;
                  })()}
                  offerText={(() => {
                    const isLatestForThisOffer =
                      !!message.offer_id &&
                      latestOfferMessageCreatedAtByOfferId[message.offer_id] === message.created_at;

                    if (isLatestForThisOffer && message.offer_id && offerById[message.offer_id]?.offer_message != null) {
                      return offerById[message.offer_id].offer_message;
                    }
                    
                    const match = /text=(.*?)(?:\||$)/.exec(message.content || '');
                    const parsedText = match ? match[1] : '';
                    console.log('ChatWindow - Using parsed offer text:', parsedText, 'for message created at:', message.created_at);
                    return parsedText;
                  })()}
                  isOwnMessage={isOwnMessage}
                  offerId={message.offer_id}
                  actionsDisabled={(() => {
                    if (!message.offer_id) return false;
                    const status = offerById[message.offer_id]?.status;
                    return !!status && status !== 'pending';
                  })()}
                  showSellerActions={(() => {
                    if (!isOwnConversationProduct) return false;
                    if (isOwnMessage) return false;
                    if (!message.offer_id) return false;

                    const isLatestForThisOffer =
                      latestOfferMessageCreatedAtByOfferId[message.offer_id] === message.created_at;
                    if (!isLatestForThisOffer) return false;

                    const offer = offerById[message.offer_id];
                    const status = offer?.status;
                    if (status && status !== 'pending') return false;

                    return true;
                  })()}
                  sellerActionLoading={!!(message.offer_id && offerDecisionLoadingId === message.offer_id)}
                  onAccept={(offerId) => handleAcceptOfferFromChat(offerId)}
                  onReject={(offerId) => handleRejectOfferFromChat(offerId)}
                  isWithdrawn={message.offer_id ? withdrawnOfferIds.has(message.offer_id) : false}
                  isSuperseded={(() => {
                    if (!latestOwnOfferCreatedAt) return false;
                    // Gray out any older own offers compared to latestOwnOfferCreatedAt
                    const isOwn = message.sender_id === user?.id;
                    const isOffer = message.message_type === 'offer' || (message.content || '').startsWith('NEW_OFFER');
                    if (!isOwn || !isOffer) return false;
                    try {
                      return new Date(message.created_at).getTime() < new Date(latestOwnOfferCreatedAt).getTime();
                    } catch {
                      return false;
                    }
                  })()}
                  onWithdraw={(offerId) => {
                    console.log('ChatWindow - Show my offers popup, initiated from offer:', offerId);
                    setWithdrawingOfferId(offerId);
                    setMyOffersOpen(true);
                  }}
                  onEdit={async (offerId) => {
                    console.log('Edit offer clicked for', offerId);
                    try {
                      // Get the current offer data
                      const offer = offerById[offerId];
                      if (offer) {
                        setEditingOfferId(offerId);
                        setEditingOfferData({
                          price: offer.offer_price,
                          message: offer.offer_message
                        });
                        setEditOfferOpen(true);
                      } else {
                        // Fetch offer data if not in cache
                        const offerData = await OffersService.getOfferById(offerId);
                        if (offerData) {
                          setEditingOfferId(offerId);
                          setEditingOfferData({
                            price: offerData.offer_price,
                            message: offerData.offer_message
                          });
                          setEditOfferOpen(true);
                        } else {
                          setError('Could not load offer data for editing');
                        }
                      }
                    } catch (e: any) {
                      console.error('Failed to load offer for editing:', e);
                      const errorMessage = e?.message || e?.error || 'Failed to load offer data';
                      setError(`Edit failed: ${errorMessage}`);
                    }
                  }}
                  onReply={() => {
                    // Focus the input for quick reply
                    const input = document.querySelector('textarea');
                    (input as HTMLTextAreaElement | null)?.focus();
                  }}
                />
                {/* Offer Message Status */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
                  mt: 0.5,
                  gap: 0.5,
                  px: 1,
                }}>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                    }}
                  >
                    {formatMessageTime(message.created_at)}
                  </Typography>
                  {isOwnMessage && (
                    <CheckIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  )}
                </Box>
              </Box>
            ) : (
              // Text Message
              <Paper
                elevation={0}
                sx={(t) => ({
                  px: 2,
                  py: 1.35,
                  color: isOwnMessage ? 'common.white' : 'text.primary',
                  borderRadius: 18,
                  borderTopLeftRadius: 18,
                  borderTopRightRadius: 18,
                  borderBottomLeftRadius: isOwnMessage ? 18 : 8,
                  borderBottomRightRadius: isOwnMessage ? 8 : 18,
                  position: 'relative',
                  background: isOwnMessage
                    ? `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.primary.dark} 100%)`
                    : `linear-gradient(180deg, ${t.palette.common.white} 0%, ${t.palette.grey[50]} 100%)`,
                  border: isOwnMessage
                    ? `1px solid ${t.palette.primary.main}22`
                    : `1px solid ${t.palette.divider}`,
                  boxShadow: isOwnMessage
                    ? `0 14px 34px ${t.palette.primary.main}33`
                    : '0 10px 26px rgba(15,23,42,0.08)',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: isOwnMessage
                      ? `0 18px 44px ${t.palette.primary.main}3D`
                      : '0 14px 34px rgba(15,23,42,0.10)',
                  },
                  transition: 'all 0.18s ease',
                })}
              >
                <Typography
                  variant="body1"
                  sx={{
                    wordBreak: 'break-word',
                    fontWeight: 650,
                    lineHeight: 1.45,
                    fontSize: '0.98rem',
                    letterSpacing: 0.1,
                  }}
                >
                  {message.content}
                </Typography>
                
                {/* Message Status */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'flex-end',
                  mt: 1,
                  gap: 0.5,
                }}>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: isOwnMessage ? 'rgba(255,255,255,0.78)' : 'text.secondary',
                      fontSize: '0.7rem',
                      fontWeight: 800,
                    }}
                  >
                    {formatMessageTime(message.created_at)}
                  </Typography>
                  {isOwnMessage && (
                    <CheckIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.7)' }} />
                  )}
                </Box>
              </Paper>
            )}
          </Box>

          {/* Own message avatar */}
          {isOwnMessage && (
            <Box sx={{ ml: 1, mb: 1 }}>
              {showAvatar ? (
                <UserAvatar
                  src={(user as any)?.avatar}
                  name={(user as any)?.name}
                  email={(user as any)?.email}
                  size={32}
                  sx={{ bgcolor: 'primary.main' }}
                />
              ) : (
                <Box sx={{ width: 32, height: 32 }} />
              )}
            </Box>
          )}
        </Box>

        {/* Time separator */}
        {showTime && (
          <Box sx={{ px: 2, my: 1.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Box sx={{ flex: 1, height: 1, bgcolor: 'divider', opacity: 0.5 }} />
              <Box
                sx={{
                  px: 1.25,
                  py: 0.35,
                  borderRadius: 999,
                  bgcolor: 'rgba(255,255,255,0.75)',
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0 8px 18px rgba(15,23,42,0.06)',
                }}
              >
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800, letterSpacing: 0.2 }}>
                  {formatMessageTime(message.created_at)}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, height: 1, bgcolor: 'divider', opacity: 0.5 }} />
            </Box>
          </Box>
        )}
      </motion.div>
    );
  };

  if (!conversation) {
    return (
      <Box sx={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'grey.50',
        p: 4,
      }}>
        <Box sx={{ textAlign: 'center', maxWidth: 400 }}>
          <MessageIcon sx={{ fontSize: 80, color: 'grey.400', mb: 2 }} />
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Welcome to Messages
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Select a conversation from the sidebar to start messaging, or click "Send Vendor Message" on any product page.
          </Typography>
          <Button
            variant="contained"
            startIcon={<StoreIcon />}
            onClick={() => window.location.href = '/all-products'}
            sx={{ px: 3 }}
          >
            Browse Products
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      flex: 1, 
      display: 'flex', 
      flexDirection: 'column',
      bgcolor: 'white',
      height: '100%',
      overflow: 'hidden',
      minHeight: 0, // Important for flex children
    }}>
      {/* Collapsible Header */}
      <AnimatePresence>
        {headerOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            style={{ overflow: 'hidden' }}
          >
            <Paper
              elevation={3}
              sx={{
                m: 1,
                mb: 1.5,
                borderRadius: 2,
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(25, 118, 210, 0.1)',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ 
                p: 1.5, 
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                flexWrap: 'wrap',
              }}>
                {onBack && (
                  <IconButton 
                    onClick={onBack} 
                    size="small"
                    sx={{ display: { xs: 'flex', md: 'none' } }}
                  >
                    <BackIcon />
                  </IconButton>
                )}
                
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: 'rgba(0,0,0,0.06)',
                    border: '1px solid rgba(0,0,0,0.08)',
                    flexShrink: 0,
                    boxShadow: '0 10px 26px rgba(15,23,42,0.14)',
                  }}
                >
                  {productThumbSrc ? (
                    <Box
                      component="img"
                      src={productThumbSrc}
                      alt={conversation.product?.name || conversation.subject || 'Product'}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                  ) : (
                    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <StoreIcon sx={{ fontSize: 26, color: 'text.secondary' }} />
                    </Box>
                  )}
                </Box>
                
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <Typography 
                      variant="subtitle1" 
                      fontWeight={600}
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {partnerUserInfo?.alias || partnerUserInfo?.first_name || conversation.other_user?.firstName || 'User'}
                    </Typography>
                    <Chip
                      label="Vendor"
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.65rem', px: 0.5 }}
                    />
                    <OnlineIcon sx={{ fontSize: 14, color: 'success.main' }} />
                    <Typography variant="caption" color="text.secondary">
                      Online
                    </Typography>
                  </Box>
                  {conversation.subject && (
                    <Typography 
                      variant="caption" 
                      color="primary.main" 
                      fontWeight={500}
                      sx={{
                        display: 'block',
                        mt: 0.25,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {conversation.subject}
                    </Typography>
                  )}
                  {conversation.product && (
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{
                        display: 'block',
                        mt: 0.25,
                        fontStyle: 'italic',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {conversation.product.name}
                    </Typography>
                  )}
                </Box>

                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title={reportDisabled ? 'Report is available once the conversation is loaded' : 'Report user'} arrow>
                    <span>
                      <IconButton
                        size="small"
                        disabled={reportDisabled}
                        onClick={() => setReportDialogOpen(true)}
                        sx={{
                          color: 'text.secondary',
                          '&:hover': {
                            background: 'rgba(239, 68, 68, 0.10)',
                            color: 'error.main',
                            transform: 'scale(1.1)'
                          },
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <FlagIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="More Options" arrow>
                    <IconButton 
                      size="small"
                      onClick={() => setChatInfoOpen(true)}
                      sx={{
                        color: 'text.secondary',
                        '&:hover': {
                          background: 'rgba(25, 118, 210, 0.1)',
                          color: 'primary.main',
                          transform: 'scale(1.1)'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Paper>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Messages Area */}
      <Box sx={{ 
        flex: 1, 
        overflowY: 'auto',
        overflowX: 'hidden',
        bgcolor: 'grey.50',
        backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(0,0,0,0.05) 1px, transparent 0)',
        backgroundSize: '20px 20px',
        minHeight: 0, // Important for flex scrolling
        position: 'relative',
        pb: 9,
        // Custom scrollbar styling
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'rgba(0,0,0,0.05)',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(0,0,0,0.2)',
          borderRadius: '4px',
          '&:hover': {
            background: 'rgba(0,0,0,0.3)',
          },
        },
        // Firefox scrollbar
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(0,0,0,0.2) rgba(0,0,0,0.05)',
      }}>
        {typeof headerOpenProp !== 'boolean' && !onHeaderOpenChange && (
          <Box sx={{
            position: 'fixed',
            top: 16,
            left: 72,
            zIndex: 25,
          }}>
            <Tooltip title={headerOpen ? "Hide Info" : "Show Info"} placement="right">
              <IconButton
                onClick={() => setHeaderOpen(!headerOpen)}
                sx={{
                  width: 46,
                  height: 46,
                  borderRadius: 999,
                  bgcolor: 'background.paper',
                  border: `1px solid rgba(0,0,0,0.08)`,
                  boxShadow: '0 10px 26px rgba(15,23,42,0.18)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  color: 'primary.main',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
                  '&:hover': {
                    bgcolor: 'background.paper',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 14px 34px rgba(15,23,42,0.22)',
                  },
                }}
              >
                {headerOpen ? <ExpandLessIcon /> : <InfoIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        )}
        {(!initialLoadComplete || loading) ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%' 
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                Loading messages...
              </Typography>
            </Box>
          </Box>
        ) : error ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <Button onClick={loadMessages} variant="outlined">
              Retry
            </Button>
          </Box>
        ) : messages.length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '100%',
            p: 4,
            textAlign: 'center',
          }}>
            <MessageIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No messages yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Start the conversation by sending a message below
            </Typography>
          </Box>
        ) : (
          <Box sx={{ 
            p: 1,
            minHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}>
            <Box sx={{ flex: 1 }}>
              <AnimatePresence>
                {messages.map((message, index) => renderMessage(message, index))}
              </AnimatePresence>
            </Box>
            <div ref={messagesEndRef} />
          </Box>
        )}
      </Box>

      {/* Message Input */}
      <Box sx={{ 
        p: 1, 
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'white',
        flexShrink: 0,
        minHeight: 'fit-content',
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1300,
        boxShadow: '0 -10px 30px rgba(15,23,42,0.08)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap' }}>
          {conversation?.product?.id && !isOwnConversationProduct && (
            <Tooltip title="Make an offer" arrow>
              <Button
                onClick={handleOpenOffer}
                disabled={offerLoading || composerDisabled}
                startIcon={<OfferIcon />}
                sx={(t) => ({
                  minWidth: 0,
                  px: 1.25,
                  py: 0.75,
                  borderRadius: 999,
                  textTransform: 'none',
                  fontWeight: 800,
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                  color: '#fff',
                  background: `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
                  boxShadow: `0 10px 26px ${t.palette.primary.main}33`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${t.palette.primary.dark} 0%, ${t.palette.secondary.dark} 100%)`,
                    boxShadow: `0 12px 32px ${t.palette.primary.main}44`,
                    transform: 'translateY(-1px)',
                  },
                  '&:disabled': {
                    background: t.palette.action.disabledBackground,
                    color: t.palette.action.disabled,
                    boxShadow: 'none',
                  },
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease',
                })}
              >
                Angebot
              </Button>
            </Tooltip>
          )}

          {/* Single Attachment Button */}
          <Tooltip title="Attach File" arrow>
            <IconButton 
              onClick={handleAttachmentClick}
              size="small"
              disabled={composerDisabled}
              sx={{
                color: 'primary.main',
                '&:hover': {
                  background: 'rgba(25, 118, 210, 0.1)',
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.2s ease',
                borderRadius: 2,
              }}
            >
              <AttachFileIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Emoji Button - Moved to left */}
          <Tooltip title="Emoji" arrow>
            <IconButton
              size="small"
              onClick={() => setEmojiPickerOpen(true)}
              disabled={composerDisabled}
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  color: 'primary.main',
                  background: 'rgba(25, 118, 210, 0.08)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <EmojiIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          {/* Hidden file inputs */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageSelect}
            style={{ display: 'none' }}
          />
          
          <input
            ref={documentInputRef}
            type="file"
            accept=".doc,.docx,.ppt,.pptx,.xls,.xlsx,.pdf,.txt,.csv,.odt,.odp,.ods,.rtf,.json,.xml"
            onChange={handleDocumentSelect}
            style={{ display: 'none' }}
          />
          
          <input
            ref={videoInputRef}
            type="file"
            accept="video/*"
            onChange={handleVideoSelect}
            style={{ display: 'none' }}
          />
          
          {/* Text Input */}
          <TextField
            fullWidth
            placeholder="Type a message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={composerDisabled}
            size="small"
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: 'grey.50',
                height: '36px',
                '&:hover': {
                  bgcolor: 'grey.100',
                },
                '&.Mui-focused': {
                  bgcolor: 'white',
                  boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.1)',
                },
                '& .MuiOutlinedInput-input': {
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  whiteSpace: 'nowrap',
                  height: '100%',
                  padding: '8px 12px',
                  '&::-webkit-scrollbar': {
                    height: '4px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'rgba(0, 0, 0, 0.2)',
                    borderRadius: '2px',
                    '&:hover': {
                      background: 'rgba(0, 0, 0, 0.3)',
                    },
                  },
                },
              },
            }}
          />
          
          {/* Send Button */}
          <IconButton
            color="primary"
            onClick={handleSendMessage}
            disabled={!newMessage.trim() || composerDisabled}
            size="small"
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              width: 36,
              height: 36,
              '&:hover': {
                bgcolor: 'primary.dark',
                transform: 'scale(1.05)',
              },
              '&:disabled': {
                bgcolor: 'grey.300',
                color: 'grey.500',
              },
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
            }}
          >
            {sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon fontSize="small" />}
          </IconButton>
        </Box>
      </Box>

      {/* Attachment Popup */}
      <AttachmentPopup
        open={attachmentPopupOpen}
        onClose={() => setAttachmentPopupOpen(false)}
        onSelectImage={handleFileInputClick}
        onSelectVideo={handleVideoInputClick}
        onSelectDocument={handleDocumentInputClick}
        onSelectFile={handleDocumentInputClick}
      />

      {/* Emoji Picker */}
      <EmojiPicker
        open={emojiPickerOpen}
        onClose={() => setEmojiPickerOpen(false)}
        onEmojiSelect={(emoji) => {
          setNewMessage(prev => prev + emoji);
        }}
      />

      {/* Chat Info Popup */}
      <ChatInfoPopup
        open={chatInfoOpen}
        onClose={() => setChatInfoOpen(false)}
        conversation={conversation}
      />

      <ReportConversationDialog
        open={reportDialogOpen}
        onClose={() => setReportDialogOpen(false)}
        onSubmit={handleSubmitConversationReport}
        partnerName={partnerUserInfo?.alias || partnerUserInfo?.first_name || conversation?.other_user?.firstName || 'User'}
        conversationSubject={conversation?.subject}
      />

      {/* Image Preview Popup */}
      <ImagePreviewPopup
        open={imagePreviewOpen}
        onClose={() => {
          setImagePreviewOpen(false);
          setSelectedImageFile(null);
          setImagePreview(null);
          setImageUploadError(null);
        }}
        onSend={handleImageSend}
        imageFile={selectedImageFile}
        imagePreview={imagePreview}
        loading={imageUploading}
        error={imageUploadError}
      />

      {/* Document Preview Popup */}
      <DocumentPreviewPopup
        open={documentPreviewOpen}
        onClose={() => {
          setDocumentPreviewOpen(false);
          setSelectedDocumentFile(null);
          setDocumentPreview(null);
          setDocumentUploadError(null);
        }}
        onSend={handleDocumentSend}
        documentFile={selectedDocumentFile}
        documentPreview={documentPreview}
        loading={documentUploading}
        error={documentUploadError}
      />

      {/* Video Preview Popup */}
      <VideoPreviewPopup
        open={videoPreviewOpen}
        onClose={() => {
          setVideoPreviewOpen(false);
          setSelectedVideoFile(null);
          setVideoPreview(null);
          setVideoUploadError(null);
        }}
        onSend={handleVideoSend}
        videoFile={selectedVideoFile}
        loading={videoUploading}
        error={videoUploadError}
      />

      {/* Offer Status Popup */}
      {offerStatusData && (
        <OfferStatusPopup
          open={offerStatusPopupOpen}
          onClose={() => setOfferStatusPopupOpen(false)}
          productName={offerStatusData?.productName || ''}
          productPrice={offerStatusData?.productPrice || 0}
          acceptedOfferPrice={offerStatusData?.acceptedOfferPrice}
          sellerName={offerStatusData?.sellerName || ''}
          reason={offerStatusData?.reason || ''}
        />
      )}

      <CompleteTransactionPopup
        open={paymentModalOpen}
        onClose={() => {
          setPaymentModalOpen(false);
          setSelectedOrderForPayment(null);
        }}
        order={selectedOrderForPayment ? {
          id: selectedOrderForPayment.id,
          product_id: selectedOrderForPayment.product_id,
          seller_id: selectedOrderForPayment.seller_id,
          purchase_price: selectedOrderForPayment.purchase_price,
        } : null}
        onPaymentSuccess={async () => {
          try {
            await loadMessages();
          } catch {
            // ignore
          }
        }}
      />

      {/* Offer Popup */}
      {conversation?.product && (
        <OfferPopup
          open={offerOpen}
          onClose={() => setOfferOpen(false)}
          onSubmit={handleOfferSubmit}
          productTitle={conversation.product.name}
          productPrice={conversation.product.price}
          sellerName={partnerUserInfo?.alias || partnerUserInfo?.first_name || conversation.other_user?.firstName || 'Vendor'}
          loading={offerLoading}
        />
      )}

      {/* Edit Offer Popup */}
      {conversation?.product && editingOfferId && editingOfferData && (
        <OfferPopup
          open={editOfferOpen}
          onClose={() => {
            setEditOfferOpen(false);
            setEditingOfferId(null);
            setEditingOfferData(null);
          }}
          onSubmit={handleEditOfferSubmit}
          productTitle={conversation.product.name}
          productPrice={conversation.product.price}
          sellerName={partnerUserInfo?.alias || partnerUserInfo?.first_name || conversation.other_user?.firstName || 'Vendor'}
          loading={offerLoading}
          mode="edit"
          initialPrice={editingOfferData.price}
          initialMessage={editingOfferData.message}
          offerId={editingOfferId}
        />
      )}

      {/* My Offers Popup */}
      <MyOffersPopup
        open={myOffersOpen}
        onClose={() => {
          setMyOffersOpen(false);
          setWithdrawingOfferId(null);
        }}
        highlightOfferId={withdrawingOfferId}
        onOfferDeleted={(offerId) => {
          setWithdrawnOfferIds(prev => {
            const next = new Set(prev);
            if (offerId) next.add(offerId);
            return next;
          });
        }}
      />
    </Box>
  );
};

export default ChatWindow;
